import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { WeeklyTimeSpan } from 'apis/restaurant-api/merchant-admin/menu2/types';
import {
  fromWeeklyScheduleInputEntriesToWeeklySchedule,
  fromWeeklyScheduleToTimePairsByWeekday,
  fromWeeklyTimeSpansToWeeklyScheduleInputEntries,
} from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';
import time from 'common/utils/time';
import { WEEKDAYS } from 'common/utils/time/values';

type Props = {
  className?: string;
  weeklySchedule: WeeklyTimeSpan[] | null;
};

const weekdays: { [key in Weekday]: string } = {
  monday: 'week.mon',
  tuesday: 'week.tue',
  wednesday: 'week.wed',
  thursday: 'week.thu',
  friday: 'week.fri',
  saturday: 'week.sat',
  sunday: 'week.sun',
};

const Root = styled.div`
  flex: 1 1 auto;
  max-width: 250px;
`;

const TimesForDay = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TimePairs = styled.div`
  display: flex;
  flex-direction: column;
  text-align: end;
`;

const WeeklyScheduleView = ({ className, weeklySchedule }: Props) => {
  const timePairsForWeekdays = useMemo(() => {
    if (weeklySchedule === null) {
      return null;
    }
    const weeklyScheduleInputEntries = fromWeeklyTimeSpansToWeeklyScheduleInputEntries(weeklySchedule);
    const schedule = fromWeeklyScheduleInputEntriesToWeeklySchedule(weeklyScheduleInputEntries);

    return fromWeeklyScheduleToTimePairsByWeekday(schedule);
  }, [weeklySchedule]);

  if (timePairsForWeekdays === null) {
    return <FormattedMessage id="time-of-week.always" />;
  }

  if (WEEKDAYS.every(day => timePairsForWeekdays[day].length === 0)) {
    return <FormattedMessage id="time-of-week.never" />;
  }

  return (
    <Root className={className}>
      {WEEKDAYS.map(day => (
        <TimesForDay key={day}>
          <FormattedMessage id={weekdays[day]} />
          <TimePairs>
            {timePairsForWeekdays[day].length > 0
              ? timePairsForWeekdays[day].map((pair, index) => (
                  <T.Body3 key={index}>
                    {time.renderMinutesFromMidnight(pair.start || 0, false)}-
                    {time.renderMinutesFromMidnight(pair.end || 0, true)}
                  </T.Body3>
                ))
              : '-'}
          </TimePairs>
        </TimesForDay>
      ))}
    </Root>
  );
};

export default WeeklyScheduleView;
